import AbstractMetadata from "../AbstractMetadata";

class PicklistMetadata extends AbstractMetadata {
    bounded: boolean = false;
    enterpriseComponent: string | null = null;
    longList: boolean = false;
    noDelete: boolean = true;
    noUpdate: boolean = true;
    noInsert: boolean = true;
    noMerge: boolean = true;
    useRealm: boolean = true;
    searchSpecification: object | null = null;
    // Drop the record being edited from its own option list -- for a self-referencing picker, where
    // choosing yourself would make you your own parent.
    excludeCurrentRecordValue: boolean = false;
    sortSpecification: object | null = null;
    static: boolean = true;
    typeField: string | null = "Pick Type";
    typeValue: string | null = null;
    typeValue2: string | null = null;
    visibilityType: string | null = null;
    // Server-side search-as-you-type for long EC-backed lists: instead of pre-loading options and
    // filtering client-side (capped at the EC's default cache size), PlatformComboBox re-queries the
    // backend as the user types, filtering by the typeField with serverFilterOperator and
    // serverFilterLimit rows. Only applies to non-static (EC-backed) picklists rendered as ComboBox.
    serverFilter: boolean = false;
    serverFilterOperator: string = "Contains";   // Contains | Starts with | Is equal to | ...
    serverFilterLimit: number = 50;
    // ComboBox: require the entry to resolve to a real option (no free-form values stored). Use for
    // value/USID-backed pickers (e.g. a relationship Child term) where an arbitrary string is not a
    // valid value. The input still type-filters; the stored value is set only on a matching option.
    mustMatchOption: boolean = false;
    allowNoSelection: boolean = false;
    allowNoSelectionDisplayValue: string | null= "None";
    allowNoSelectionValue: boolean = false;
    // When set, options are sourced client-side from sessionContext.getAccount()[companySource]
    // (e.g. "Positions" / "UserGroups" / "WorkGroups") instead of from pick values or an EC.
    companySource: string | null = null;
    // Form-coupled exclusion: drop any dropdown option whose value equals the CURRENT value of the
    // named form field(s). PlatformSelect reads the values off its `parent` (the PlatformDetail form) and
    // re-applies on every render, so it reacts as the other field changes. e.g. ["ParentUSID"] on a
    // Child picker so a term can't be related to itself.
    excludeFormFields: string[] | null = null;

    // Like excludeFormFields, but the value(s) to exclude are read from the form EC's own
    // attributes rather than a rendered control. Use when the value to exclude is not something the
    // user picks on the form (so it can't be a membership control) but is stamped onto the EC — e.g.
    // a synonym picker excluding the source term's own EnterpriseTermUSID (set on the TermSynonym EC
    // in the form applet's setAction). PlatformSelect reads getAttributeValue(name) off parent.applet's EC.
    excludeEnterpriseComponentAttributes: string[] | null = null;

    // Indented-hierarchy dropdown: when true, PlatformSelect orders the (EC-sourced) options as a tree
    // and indents each MenuItem by its depth, instead of a flat list. hierarchyParentField names the
    // buffer field on each option that holds its PARENT's value (which must equal another option's
    // typeValue) — e.g. "ParentUSID" for Positions. Options whose parent is null/absent (or whose
    // parent was filtered out of the list) become roots. Value model is unchanged (still typeValue).
    hierarchical: boolean = false;
    hierarchyParentField: string | null = null;

    // Optional secondary value appended to each option's display, e.g. "Name (Primary User: Jane Doe)".
    // secondaryDisplayField names the buffer field to read; secondaryDisplayLabel is the "(<label>: …)"
    // prefix (omit the label to render just "(<value>)"). Only appended when the field has a value.
    secondaryDisplayField: string | null = null;
    secondaryDisplayLabel: string | null = null;

    getHierarchical(): boolean {
        return this.hierarchical;
    }

    getHierarchyParentField(): string | null {
        return this.hierarchyParentField;
    }

    getSecondaryDisplayField(): string | null {
        return this.secondaryDisplayField;
    }

    getSecondaryDisplayLabel(): string | null {
        return this.secondaryDisplayLabel;
    }

    getExcludeEnterpriseComponentAttributes(): string[] | null {
        return this.excludeEnterpriseComponentAttributes;
    }

    getBounded(): boolean {
        return this.bounded;
    };

    getEnterpriseComponent(): string | null {
        return this.enterpriseComponent;
    };

    getLongList(): boolean {
        return this.longList;
    };

    getNoDelete(): boolean {
        return this.noDelete;
    };

    getNoInsert(): boolean {
        return this.noInsert;
    };

    getNoUpdate(): boolean {
        return this.noUpdate;
    };

    getNoMerge() : boolean {
        return this.noMerge;
    };

    getUseRealm() : boolean {
        return this.useRealm;
    };

    getExcludeCurrentRecordValue(): boolean {
        return this.excludeCurrentRecordValue;
    }

    getSearchSpecification(): object | null {
        return this.searchSpecification;
    };

    getSortSpecification(): object | null {
        return this.sortSpecification;
    };

    getStatic(): boolean {
        return this.static;
    };

    getTypeField(): string | null {
        return this.typeField;
    };

    getTypeValue(): string | null {
        return this.typeValue;
    };

    getTypeValue2(): string | null {
        return this.typeValue2;
    };

    getVisibilityType(): string | null {
        return this.visibilityType;
    }

    getServerFilter(): boolean {
        return this.serverFilter;
    }

    getServerFilterOperator(): string {
        return this.serverFilterOperator;
    }

    getServerFilterLimit(): number {
        return this.serverFilterLimit;
    }

    getMustMatchOption(): boolean {
        return this.mustMatchOption;
    };

    getAllowNoSelection(): boolean {
        return this.allowNoSelection;
    };

    getAllowNoSelectionDisplayValue(): string | null {
        return this.allowNoSelectionDisplayValue;
    }

    getCompanySource(): string | null {
        return this.companySource;
    };

    getExcludeFormFields(): string[] | null {
        return this.excludeFormFields;
    };

    getAllowNoSelectionValue(): boolean {
        return this.allowNoSelectionValue;
    };
}

export default PicklistMetadata;
